import {
  isAbbyyFlow,
  isDocument,
  Documents,
} from "@ca-dmv-radv/screens/src/dashboard/helpers/constants";
import { useTranslation } from "@ca-dmv-radv/translation";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isMobile } from "react-device-detect";

import { getExpirationTranslationKey } from "@ca-dmv-radv/utilities/src/getExpirationTranslationKey";
import {
  isApplicationNch,
  isApplicationRnc,
} from "@ca-dmv-radv/screens/src/constants";
import { uploadFile } from "../api";
import { useApplication } from "../application-context-provider";
import { useThrowFetchError } from "../error-context-provider";

const FileUploadContext = React.createContext();

const getBirthCertificateFormDataToSubmit = (
  originalBirthCertificateFormData
) => {
  const birthCertificateFormData = { ...originalBirthCertificateFormData };
  if (birthCertificateFormData.State_File_Nbr_Chk === "true") {
    birthCertificateFormData.State_File_Number = "";
  }

  if (birthCertificateFormData.Registrar_Nbr_Chk === "true") {
    birthCertificateFormData.Registrar_Number = "";
  }

  return birthCertificateFormData;
};

const defaultBirthCertificateFormData = {
  State_Of_Birth: "",
  County_Of_Birth: "",
  Mother_Maiden_Name: "",
  State_File_Number: "",
  Registrar_Number: "",
  Gender: "",
  uploadDocument: "",
  State_File_Nbr_Chk: "false",
  Registrar_Nbr_Chk: "false",
};

export const defaultPassportFormData = {
  Passport_Number: "",
  Confirm_Passport_Number: "",
  Passport_Expiration_Date: "",
};

export const defaultExpirationDateFormData = {
  Expiration_Date: "",
};

export function FileUploadContextProvider({ children }) {
  const [files, setFiles] = useState({});
  const [addingAdditionalPage, setAddingAdditionalPage] = useState(false);
  const [birthCertificateFormData, setBirthCertificateFormData] = useState(
    defaultBirthCertificateFormData
  );
  const [passportFormData, setPassportFormData] = useState(
    defaultPassportFormData
  );
  const [expirationDateFormData, setExpirationDateFormData] = useState(
    defaultExpirationDateFormData
  );
  const [showInputError, setShowInputError] = useState(false);
  const throwFetchError = useThrowFetchError();
  const { t } = useTranslation();
  const { maxFileSize, applicationType, documentTypes, socureToken } =
    useApplication();
  const isNchOrRnc =
    isApplicationNch(applicationType) || isApplicationRnc(applicationType);

  /**
   * Sends file uploads to the API.
   */
  const postUploads = useCallback(
    async (documentUpload) => {
      const filesToUpload = files[documentUpload.id];

      if (!filesToUpload) {
        return true;
      }

      let success;

      let formData = {};

      if (
        isDocument(
          Documents.BIRTH_CERTIFICATE,
          documentTypes[documentUpload.catDocId]
        )
      ) {
        formData = getBirthCertificateFormDataToSubmit(
          birthCertificateFormData
        );
      } else if (
        isDocument(
          Documents.PASSPORT,
          documentTypes[documentUpload.catDocId]
        ) ||
        isDocument(
          Documents.PASSPORT_CARD,
          documentTypes[documentUpload.catDocId]
        )
      ) {
        formData = passportFormData;
      } else if (documentUpload.requiredExpirationDate) {
        formData = expirationDateFormData;
      }

      let mwcSourceId = 1;
      if (isMobile) {
        if (isAbbyyFlow(documentUpload.ATD)) {
          mwcSourceId = 1;
        } else {
          mwcSourceId = 2;
        }
      }

      try {
        ({ success } = await uploadFile(
          documentUpload,
          filesToUpload.map(({ original }) => original),
          formData,
          mwcSourceId,
          applicationType,
          documentTypes,
          maxFileSize,
          socureToken
        ));

        if (!success) {
          throw new Error();
        }
      } catch (error) {
        if (
          error?.response?.data?.message ===
          "Issue with the Provided dataValidation failed: expirationDate is outside the allowed window."
        ) {
          const { key: translationKey, default: defaultString } =
            getExpirationTranslationKey(
              documentTypes[documentUpload.catDocId],
              isNchOrRnc
            );
          throwFetchError({
            error,
            message: t(translationKey, defaultString),
          });
        } else {
          throwFetchError({
            error,
            message: t(
              "app-error-fetchingData-postUploads",
              "There was an error uploading your documents. Try again."
            ),
          });
        }
      }
      // make a call from here if success for authenticateDocument --- one approach to try
      return success;
    },
    [
      files,
      birthCertificateFormData,
      passportFormData,
      expirationDateFormData,
      maxFileSize,
      throwFetchError,
      t,
      socureToken,
    ]
  );

  return (
    <FileUploadContext.Provider
      value={useMemo(
        () => ({
          addingAdditionalPage,
          birthCertificateFormData,
          files,
          passportFormData,
          showInputError,
          expirationDateFormData,
          postUploads,
          setAddingAdditionalPage,
          setBirthCertificateFormData,
          setFiles,
          setShowInputError,
          setPassportFormData,
          setExpirationDateFormData,
          resetFormData: () => {
            setBirthCertificateFormData(defaultBirthCertificateFormData);
            setPassportFormData(defaultPassportFormData);
            setExpirationDateFormData(defaultExpirationDateFormData);
          },
        }),
        [
          addingAdditionalPage,
          birthCertificateFormData,
          files,
          passportFormData,
          expirationDateFormData,
          postUploads,
          showInputError,
        ]
      )}
    >
      {children}
    </FileUploadContext.Provider>
  );
}

export function useFileUpload() {
  return useContext(FileUploadContext);
}
